/**
 * Where the Properties panel's state edits and previews act: on the group
 * that owns a state, through the strip's states session when the strip is
 * editing that group, or on the panel's own object for a pointer-driven
 * state.
 */

import { Properties } from 'trace-viewer/behaviors';
import type { SceneObject } from 'trace-viewer/scene';
import { resolveStateOwner, type StatesSession } from 'trace-viewer/editor';
import { getStatesSession } from '../states-session';

/**
 * The object a state named `name` lives on, seen from `obj`: the group owner
 * when it carries the state, as the strip shows it, else `obj` (a state only
 * it has). The panel's previews pose from it, and its rename and delete act
 * on it.
 */
export function stateHome(obj: SceneObject, name: string): SceneObject {
    const owner = resolveStateOwner(obj);
    return owner.getBehavior(Properties)?.getStateNames().includes(name) ? owner : obj;
}

/**
 * The strip's states session when the strip's context is `owner`, else null.
 * An edit through it keeps the state the strip shows and page looks; any other
 * group's edit goes to the subtree action.
 */
export function stripSessionFor(owner: SceneObject): StatesSession | null {
    const session = getStatesSession();
    return session?.getContext() === owner ? session : null;
}

/**
 * A pseudo or compound state (`:hover`, `gold:hover`): any name with a pseudo
 * part. It is pointer-driven on each object, and no setState names it, so an
 * edit to it changes only the panel's object.
 */
export function isObjectLocal(name: string): boolean {
    return name.includes(':');
}
